import { useEffect, useState } from "react";
import type { WarrantyTemplate } from "@shared/modules/x-garanties";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Field, Input } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { formatDateTime } from "../../../core/lib/format";
import { TermsEditor } from "../components/terms-editor";
import { listTemplates, updateTemplate } from "../lib/api";

/** Modèle de termes (admin) : texte markdown repris par chaque nouvelle garantie, intervalle d'entretien. */
export function GarantieModelePage() {
  const push = useToastStore((s) => s.push);
  const [template, setTemplate] = useState<WarrantyTemplate | null>(null);
  const [name, setName] = useState("");
  const [termsMd, setTermsMd] = useState("");
  const [months, setMonths] = useState("12");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    listTemplates()
      .then((list) => {
        const t = list[0] ?? null;
        setTemplate(t);
        if (t) {
          setName(t.name);
          setTermsMd(t.termsMd);
          setMonths(String(t.maintenanceMonths));
        }
      })
      .catch(() => setFailed(true));
  }, []);

  const dirty =
    template !== null &&
    (name !== template.name ||
      termsMd !== template.termsMd ||
      months !== String(template.maintenanceMonths));

  const save = async () => {
    if (!template || busy) return;
    const maintenanceMonths = Number.parseInt(months, 10);
    if (!Number.isFinite(maintenanceMonths) || maintenanceMonths < 1 || maintenanceMonths > 120) {
      push("danger", "Intervalle d'entretien invalide", "Entre 1 et 120 mois.");
      return;
    }
    if (!name.trim()) {
      push("danger", "Le nom du modèle est obligatoire");
      return;
    }
    setBusy(true);
    try {
      const saved = await updateTemplate(template.id, {
        name: name.trim(),
        termsMd,
        maintenanceMonths,
      });
      setTemplate(saved);
      push("success", "Modèle enregistré", "Les prochaines garanties partiront de ce texte.");
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page garantie-form">
      <PageHeader
        title="Modèle de garantie"
        subtitle="Termes repris par chaque nouvelle garantie. Les garanties déjà créées gardent leur texte."
        actions={
          <Button variant="primary" disabled={busy || !dirty} onClick={() => void save()}>
            Enregistrer le modèle
          </Button>
        }
      />

      {failed ? <p className="empty">Modèle indisponible — réessayez dans un instant.</p> : null}
      {!failed && !template ? <p className="muted">Chargement…</p> : null}

      {template ? (
        <form
          className="garantie-form__body"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <section className="form-section">
            <div className="form-section__head">
              <h2 className="form-section__title">Réglages</h2>
              <p className="form-section__hint">
                Dernière modification :{" "}
                {formatDateTime(template.updatedAt, { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
            <div className="form-section__grid">
              <Field label="Nom du modèle">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field
                label="Rappel d'entretien (mois)"
                hint="La fiche affiche le prochain entretien = émission + cet intervalle."
              >
                <Input
                  type="number"
                  min={1}
                  max={120}
                  inputMode="numeric"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                />
              </Field>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section__head">
              <h2 className="form-section__title">Termes</h2>
              <p className="form-section__hint">
                Markdown : titres (##), listes (-), numérotées (1.), gras (**), citation (&gt;). Le
                document imprimé en reprend la structure.
              </p>
            </div>
            <TermsEditor value={termsMd} onChange={setTermsMd} />
          </section>

          <div className="garantie-form__actions">
            <Button type="submit" variant="primary" disabled={busy || !dirty}>
              Enregistrer le modèle
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
